import {
  Box,
  Button,
  FormControlLabel,
  Switch,
  TextField,
} from "@material-ui/core";
import { useEffect, useState } from "react";
import { tokens } from "../app/theme";
import { isJiraHost, isScheduleExpression, parseList } from "./pluginValidation";
import {
  useApplicationConfig,
  usePersistApplicationConfig,
} from "./useApplicationConfig";

interface JiraFormErrors {
  host?: string;
  syncSchedule?: string;
}

export function JiraPluginForm() {
  const config = useApplicationConfig();
  const persist = usePersistApplicationConfig();
  const jira = config.plugins.jira;

  const [enabled, setEnabled] = useState(jira.enabled);
  const [host, setHost] = useState(jira.host);
  const [syncSchedule, setSyncSchedule] = useState(jira.syncSchedule);
  const [groupIdsEM, setGroupIdsEM] = useState(jira.groupIdsEM.join("\n"));
  const [groupIdsEngineer, setGroupIdsEngineer] = useState(
    jira.groupIdsEngineer.join("\n")
  );
  const [epicField, setEpicField] = useState(
    jira.customerCommunicationEpicField || ""
  );
  const [errors, setErrors] = useState<JiraFormErrors>({});

  useEffect(() => {
    setEnabled(jira.enabled);
    setHost(jira.host);
    setSyncSchedule(jira.syncSchedule);
    setGroupIdsEM(jira.groupIdsEM.join("\n"));
    setGroupIdsEngineer(jira.groupIdsEngineer.join("\n"));
    setEpicField(jira.customerCommunicationEpicField || "");
  }, [jira]);

  function validate(): JiraFormErrors {
    const next: JiraFormErrors = {};
    const trimmedHost = host.trim().toLowerCase();
    if (enabled && !trimmedHost) {
      next.host = "Host is required when Jira is enabled";
    } else if (trimmedHost && !isJiraHost(trimmedHost)) {
      next.host = "Must be an atlassian.net hostname without protocol";
    }
    if (enabled && !isScheduleExpression(syncSchedule)) {
      next.syncSchedule = "Must be an AWS cron(...) or rate(...) expression";
    }
    return next;
  }

  function save(): void {
    const validation = validate();
    setErrors(validation);
    if (Object.keys(validation).length > 0) {
      return;
    }
    persist({
      ...config,
      plugins: {
        ...config.plugins,
        jira: {
          ...jira,
          enabled,
          host: host.trim().toLowerCase(),
          syncSchedule: syncSchedule.trim(),
          groupIdsEM: parseList(groupIdsEM),
          groupIdsEngineer: parseList(groupIdsEngineer),
          customerCommunicationEpicField: epicField.trim() || undefined,
        },
      },
    }).catch(() => {
      /* error already surfaced via app feedback */
    });
  }

  return (
    <Box display="flex" flexDirection="column" gridGap={16} marginTop={2}>
      <FormControlLabel
        control={
          <Switch
            color="primary"
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
            inputProps={{ "data-testid": "jira-enabled" } as any}
          />
        }
        label="Enabled"
      />
      <TextField
        label="Jira host"
        value={host}
        onChange={(event) => setHost(event.target.value)}
        error={!!errors.host}
        helperText={errors.host || "For example, your-org.atlassian.net"}
        variant="outlined"
        inputProps={{ "data-testid": "jira-host" }}
      />
      <TextField
        label="Sync schedule"
        value={syncSchedule}
        onChange={(event) => setSyncSchedule(event.target.value)}
        error={!!errors.syncSchedule}
        helperText={
          errors.syncSchedule || "AWS cron(...) or rate(...) expression"
        }
        variant="outlined"
        inputProps={{ "data-testid": "jira-schedule" }}
      />
      <TextField
        label="EM group IDs"
        value={groupIdsEM}
        onChange={(event) => setGroupIdsEM(event.target.value)}
        multiline
        minRows={2}
        variant="outlined"
        helperText="One group ID per line."
      />
      <TextField
        label="Engineer group IDs"
        value={groupIdsEngineer}
        onChange={(event) => setGroupIdsEngineer(event.target.value)}
        multiline
        minRows={2}
        variant="outlined"
        helperText="One group ID per line."
      />
      <TextField
        label="Customer communication epic field"
        value={epicField}
        onChange={(event) => setEpicField(event.target.value)}
        variant="outlined"
        helperText="Optional Jira custom field ID."
      />
      <Box>
        <Button
          variant="contained"
          color="primary"
          onClick={save}
          data-testid="jira-save"
        >
          Save Jira settings
        </Button>
      </Box>
      <Box style={{ color: tokens.fg3, fontSize: 12 }}>
        Jira credentials stay in secure storage and are never edited here.
      </Box>
    </Box>
  );
}
